import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  HStack,
  VStack,
  Divider,
  Box,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';

import type {
  APIRemark,
  APIRemarkFile,
  APIAppeal,
  APIBaraEligibility,
} from 'types/api.types';
import {
  ValidationColumn,
  RemarksColumn,
  FileUploadsColumn,
  CommentsSection,
  SupportingFilesSection,
  SubmissionStatusAlerts,
} from 'views/shared/ReferToUnderwriting/components';
import {
  getRemarkId,
  getFileId,
  getChildRemarks,
  getChildFiles,
} from 'views/shared/ReferToUnderwriting/helpers';
import {
  useAppealPolling,
  useAppealFiles,
} from 'views/shared/ReferToUnderwriting/hooks';
import isDeferralAllowed from 'helpers/isDeferralAllowed';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import {
  apiGetBaraEligibility,
  apiAppealToUnderwriting,
  apiSubmitBaraReferralToMP,
} from 'api';

// Labels used while enable-bara-referral-fixes is off
const LEGACY_BARA_REASON_LABELS: Record<string, string> = {
  WITHIN_HOURS: 'Branch is currently within business hours',
  EFFECTIVE_DATE_TOO_SOON:
    'Effective date is too far away — after-hours referral is only available for policies starting within the next 7 days (60 days on holidays)',
  HOLIDAY: 'Today is a holiday',
  BARA_DISABLED: 'After-hours referral is not enabled for this branch',
  BRANCH_UNRESOLVED: 'Branch could not be resolved for this policy',
};

const BARA_REASON_LABELS: Record<string, string> = {
  WITHIN_BUSINESS_HOURS: 'Branch is currently within business hours',
  EFFECTIVE_DATE_TOO_SOON:
    'Effective date is too far away — after-hours referral is only available for policies starting within the next 7 days (60 days on holidays)',
  HOLIDAY: 'Today is a holiday',
  BARA_DISABLED: 'After-hours referral is not enabled for this branch',
  BRANCH_UNRESOLVED: 'Branch could not be resolved for this policy',
  ERROR: 'Could not check after-hours eligibility',
  NOT_PERSONAL_LINES:
    'After-hours referral is available for personal lines only',
  PAYMENT_INFORMATION_MISSING:
    'Complete and save all required payment information first',
  REQUIRED_DOCUMENTS_MISSING:
    'All mandatory items must be completed before the submission can be referred.',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  validations: Validation[];
  remarks: APIRemark[];
  files: APIRemarkFile[];
  uuid: string;
  mandatoryFileValidations: string[];
  appeal?: APIAppeal | null;
  onPUSuccess: () => void;
  onMPSuccess: () => void;
  blockingConditions?: string[];
}

const BaraReferralModal = ({
  isOpen,
  onClose,
  validations,
  remarks,
  files,
  uuid,
  mandatoryFileValidations,
  appeal,
  onPUSuccess,
  onMPSuccess,
  blockingConditions = [],
}: Props) => {
  const toast = useToast();
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');
  const reasonLabels = isBaraFixesEnabled
    ? BARA_REASON_LABELS
    : LEGACY_BARA_REASON_LABELS;
  const isOwnershipOnlyDeferralEnabled = useFeatureFlag(
    'enable-ownership-only-deferral',
  );

  const [eligibility, setEligibility] = useState<APIBaraEligibility | null>(
    null,
  );
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<'pu' | 'mp' | null>(null);

  const [selectedValidations, setSelectedValidations] = useState<string[]>([]);
  const [selectedRemarks, setSelectedRemarks] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  const [hasLoadedFromHistory, setHasLoadedFromHistory] = useState(false);
  const [isSubmittingPU, setIsSubmittingPU] = useState(false);
  const [isSubmittingMP, setIsSubmittingMP] = useState(false);

  const {
    supportingFiles,
    isUploadingFile,
    deletingFileId,
    handleFilesUpload,
    handleFileRemove,
    resetFiles,
  } = useAppealFiles(uuid, isOpen, appeal?.supporting_files);

  const {
    submissionStatus,
    setSubmissionStatus,
    errorMessage,
    startPolling,
    stopPolling,
    resetPollingState,
  } = useAppealPolling(uuid);

  const problemValidations = useMemo(
    () =>
      validations.filter(
        (v) =>
          v.validation_outcome === 'caution' || v.validation_outcome === 'fail',
      ),
    [validations],
  );
  const problemRemarks = useMemo(
    () => remarks.filter((r) => r.status !== 'approved'),
    [remarks],
  );
  const problemFiles = useMemo(
    () =>
      files
        .map((f) => ({
          ...f,
          mandatory:
            mandatoryFileValidations.includes(f.validation_key) ||
            !isDeferralAllowed(
              f.validation_key,
              validations,
              isOwnershipOnlyDeferralEnabled,
            ),
        }))
        .filter((f) => f.status !== 'approved' && f.mandatory),
    [
      files,
      mandatoryFileValidations,
      validations,
      isOwnershipOnlyDeferralEnabled,
    ],
  );

  const handleClose = useCallback(() => {
    stopPolling();
    resetPollingState();
    resetFiles();
    setSelectedValidations([]);
    setSelectedRemarks([]);
    setSelectedFiles([]);
    setComments('');
    setHasLoadedFromHistory(false);
    setEligibility(null);
    setSelectedRoute(null);
    onClose();
  }, [stopPolling, resetPollingState, resetFiles, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setEligibilityLoading(true);
    setEligibility(null);
    apiGetBaraEligibility(uuid)
      .then((res: any) => setEligibility(res))
      .catch(() =>
        setEligibility({
          bara_eligible: false,
          reason: 'ERROR',
          is_holiday: false,
          timezone: '',
          local_time: '',
          local_datetime: '',
          group_blocked_by: null,
        }),
      )
      .finally(() => setEligibilityLoading(false));
  }, [isOpen, uuid]);

  useEffect(() => {
    if (isOpen && !hasLoadedFromHistory) {
      if (appeal) {
        setSelectedValidations(appeal.validations || []);
        setSelectedRemarks(appeal.remarks || []);
        setSelectedFiles(appeal.files || []);
        setComments(appeal.comments || '');
      } else {
        setSelectedValidations(problemValidations.map((v) => v.validation_id));
        setSelectedRemarks(
          problemRemarks.map((r) => `${r.validation_key}-${r.item_key}`),
        );
        setSelectedFiles(
          problemFiles.map((f) => `${f.validation_key}-${f.item_key}`),
        );
        setComments('');
      }
      setHasLoadedFromHistory(true);
    }
  }, [
    isOpen,
    hasLoadedFromHistory,
    appeal,
    problemValidations,
    problemRemarks,
    problemFiles,
  ]);

  useEffect(() => {
    if (submissionStatus === 'success') {
      onPUSuccess();
      handleClose();
    }
  }, [submissionStatus, onPUSuccess, handleClose]);

  const handleValidationToggle = (id: string) => {
    setSelectedValidations((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        const childRemarks = getChildRemarks(id, problemRemarks);
        const childFiles = getChildFiles(id, problemFiles);
        setSelectedRemarks((r) =>
          r.filter(
            (rId) => !childRemarks.some((cr) => getRemarkId(cr) === rId),
          ),
        );
        setSelectedFiles((f) =>
          f.filter((fId) => !childFiles.some((cf) => getFileId(cf) === fId)),
        );
        return prev.filter((v) => v !== id);
      }
      const childRemarks = getChildRemarks(id, problemRemarks);
      const childFiles = getChildFiles(id, problemFiles);
      setSelectedRemarks((r) => [
        ...new Set([...r, ...childRemarks.map(getRemarkId)]),
      ]);
      setSelectedFiles((f) => [
        ...new Set([...f, ...childFiles.map(getFileId)]),
      ]);
      return [...prev, id];
    });
  };

  const handleRemarkToggle = (id: string) =>
    setSelectedRemarks((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );

  const handleFileToggle = (id: string) =>
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );

  const handleSubmitPU = async () => {
    setIsSubmittingPU(true);
    setSubmissionStatus('submitting');
    try {
      const response: any = await apiAppealToUnderwriting(uuid, {
        validations: selectedValidations,
        remarks: selectedRemarks,
        files: selectedFiles,
        comments,
        supporting_files:
          supportingFiles.length > 0
            ? supportingFiles.map((f) => f.id)
            : undefined,
      });
      if (response?.appeal_log_id) {
        startPolling(response.appeal_log_id);
      } else {
        setSubmissionStatus('error');
      }
    } catch (error: any) {
      setSubmissionStatus('error');
      toast({
        title: 'Submission failed',
        description: error?.message || 'Failed to submit referral',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingPU(false);
    }
  };

  const handleSubmitMP = async () => {
    setIsSubmittingMP(true);
    try {
      const validationMessageMap = Object.fromEntries(
        validations.map((v) => [
          v.validation_id,
          v.validation_message || v.description || v.validation_id,
        ]),
      );
      const seen = new Set<string>();
      const validationObjects = [
        ...blockingConditions.map((id) => ({
          id,
          message: validationMessageMap[id] ?? id,
        })),
        ...selectedValidations.map((id) => ({
          id,
          message: validationMessageMap[id] ?? id,
        })),
      ].filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      await apiSubmitBaraReferralToMP(uuid, {
        validations: validationObjects,
        remarks: selectedRemarks,
        files: selectedFiles,
        comments,
      });
      toast({
        title: 'Referred to MP/Delegate',
        description:
          'Your submission has been referred for after-hours review. Please proceed to sign.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onMPSuccess();
      handleClose();
    } catch (error: any) {
      toast({
        title: 'Submission failed',
        description:
          (isBaraFixesEnabled && error?.detail) ||
          error?.message ||
          'Failed to submit referral',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingMP(false);
    }
  };

  const isSubmitting =
    isSubmittingPU ||
    isSubmittingMP ||
    submissionStatus === 'submitting' ||
    submissionStatus === 'polling';
  const isSubmitDisabled = !comments.trim() || isSubmitting;
  const baraEligible = eligibility?.bara_eligible === true;
  const ineligibilityReason =
    !baraEligible && eligibility
      ? (reasonLabels[eligibility.reason ?? ''] ??
        eligibility.reason ??
        'Not eligible for after-hours referral')
      : null;
  // Same mandatory-document rule the backend applies to both routes
  const puUnavailableReason =
    isBaraFixesEnabled &&
    files.some((f) => f.mandatory && (f.is_deferred || f.status !== 'approved'))
      ? BARA_REASON_LABELS.REQUIRED_DOCUMENTS_MISSING
      : null;

  // Route selection screen — shown first if no route chosen yet
  const showRouteSelection = !selectedRoute;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxW={{ base: '95vw', md: '90vw', lg: '1200px' }}>
        <ModalHeader>
          {showRouteSelection ? (
            'Refer to Underwriting'
          ) : (
            <HStack spacing={2}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  stopPolling();
                  resetPollingState();
                  setSelectedRoute(null);
                }}
                px={1}
              >
                ←
              </Button>
              <Text>
                {selectedRoute === 'mp'
                  ? 'Refer to MP / Delegate'
                  : 'Refer to Portfolio Underwriter'}
              </Text>
            </HStack>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {showRouteSelection ? (
            /* ── Step 0: choose route ── */
            <Box py={4}>
              {eligibilityLoading ? (
                <HStack spacing={2} py={4} justify="center">
                  <Spinner size="sm" color="blue.400" />
                  <Text fontSize="sm" color="gray.500">
                    Checking after-hours eligibility...
                  </Text>
                </HStack>
              ) : (
                <VStack spacing={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    Choose how you would like to refer this submission for
                    review.
                  </Text>

                  {/* MP/Delegate option */}
                  <Box
                    as="button"
                    onClick={
                      baraEligible ? () => setSelectedRoute('mp') : undefined
                    }
                    borderWidth="1px"
                    borderRadius="lg"
                    p={5}
                    textAlign="left"
                    borderColor={baraEligible ? 'purple.300' : 'gray.200'}
                    bg={baraEligible ? 'purple.50' : 'gray.50'}
                    opacity={baraEligible ? 1 : 0.6}
                    cursor={baraEligible ? 'pointer' : 'not-allowed'}
                    _hover={
                      baraEligible
                        ? { borderColor: 'purple.500', bg: 'purple.100' }
                        : {}
                    }
                    transition="all 0.15s"
                  >
                    <Text
                      fontWeight="semibold"
                      fontSize="md"
                      color={baraEligible ? 'purple.700' : 'gray.500'}
                      mb={1}
                    >
                      After-Hours — MP / Delegate
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Submit directly to the Managing Partner or Delegate for
                      after-hours review.
                    </Text>
                    {!baraEligible && eligibility && (
                      <Text fontSize="xs" color="orange.600" mt={2}>
                        Not available — {ineligibilityReason}
                      </Text>
                    )}
                    {baraEligible && eligibility?.local_time && (
                      <Text fontSize="xs" color="purple.500" mt={2}>
                        Branch local time: {eligibility.local_time} (
                        {eligibility.timezone})
                      </Text>
                    )}
                  </Box>

                  {/* PU option */}
                  <Box
                    as="button"
                    onClick={
                      puUnavailableReason
                        ? undefined
                        : () => setSelectedRoute('pu')
                    }
                    borderWidth="1px"
                    borderRadius="lg"
                    p={5}
                    textAlign="left"
                    borderColor={puUnavailableReason ? 'gray.200' : 'blue.300'}
                    bg={puUnavailableReason ? 'gray.50' : 'blue.50'}
                    opacity={puUnavailableReason ? 0.6 : 1}
                    cursor={puUnavailableReason ? 'not-allowed' : 'pointer'}
                    _hover={
                      puUnavailableReason
                        ? {}
                        : { borderColor: 'blue.500', bg: 'blue.100' }
                    }
                    transition="all 0.15s"
                  >
                    <Text
                      fontWeight="semibold"
                      fontSize="md"
                      color={puUnavailableReason ? 'gray.500' : 'blue.700'}
                      mb={1}
                    >
                      Portfolio Underwriter
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Route to the Portfolio Underwriter for standard review.
                    </Text>
                    {puUnavailableReason && (
                      <Text fontSize="xs" color="orange.600" mt={2}>
                        Not available — {puUnavailableReason}
                      </Text>
                    )}
                  </Box>
                </VStack>
              )}
            </Box>
          ) : (
            /* ── Step 1: referral form (shared for both routes) ── */
            <>
              <SubmissionStatusAlerts
                status={submissionStatus}
                errorMessage={errorMessage}
              />

              <HStack
                align="flex-start"
                spacing={{ base: 0, lg: 4 }}
                mb={6}
                flexDirection={{ base: 'column', lg: 'row' }}
              >
                <ValidationColumn
                  validations={problemValidations}
                  selectedValidations={selectedValidations}
                  onToggle={handleValidationToggle}
                />
                <Divider
                  orientation="vertical"
                  h="300px"
                  display={{ base: 'none', lg: 'block' }}
                />
                <RemarksColumn
                  remarks={problemRemarks}
                  validations={validations}
                  selectedRemarks={selectedRemarks}
                  selectedValidations={selectedValidations}
                  onToggle={handleRemarkToggle}
                />
                <Divider
                  orientation="vertical"
                  h="300px"
                  display={{ base: 'none', lg: 'block' }}
                />
                <FileUploadsColumn
                  files={problemFiles}
                  validations={validations}
                  selectedFiles={selectedFiles}
                  selectedValidations={selectedValidations}
                  onToggle={handleFileToggle}
                />
              </HStack>

              <Divider my={4} />
              <CommentsSection comments={comments} onChange={setComments} />
              <Divider my={4} />
              <SupportingFilesSection
                uploadedFiles={supportingFiles}
                onFilesUpload={handleFilesUpload}
                onFileRemove={handleFileRemove}
                isUploading={isUploadingFile}
                isDeletingFileId={deletingFileId}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter flexDirection={{ base: 'column', sm: 'row' }} gap={2}>
          <Button
            variant="ghost"
            onClick={handleClose}
            w={{ base: '100%', sm: 'auto' }}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>

          {!showRouteSelection && selectedRoute === 'mp' && (
            <Button
              colorScheme="purple"
              onClick={handleSubmitMP}
              isDisabled={isSubmitDisabled}
              isLoading={isSubmittingMP}
              loadingText="Submitting..."
              w={{ base: '100%', sm: 'auto' }}
            >
              Submit to MP / Delegate
            </Button>
          )}

          {!showRouteSelection && selectedRoute === 'pu' && (
            <Button
              colorScheme="brand"
              onClick={handleSubmitPU}
              isDisabled={isSubmitDisabled}
              isLoading={
                isSubmittingPU ||
                submissionStatus === 'submitting' ||
                submissionStatus === 'polling'
              }
              loadingText={
                submissionStatus === 'polling'
                  ? 'Creating ticket...'
                  : 'Submitting...'
              }
              w={{ base: '100%', sm: 'auto' }}
            >
              {submissionStatus === 'success'
                ? 'Submitted'
                : 'Submit to Portfolio Underwriter'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BaraReferralModal;
